/**
 * Press feedback — spring-driven touch highlight for interactive elements.
 *
 * Two flavours, both GPU-only (they touch `transform` exclusively):
 *
 *   - `pressable(el, opts?)`   JS spring: presses to `scale` on pointerdown and
 *     springs back (with momentum + slight overshoot) on release. For hero
 *     controls: FAB, tabs, primary CTAs, like buttons. Optional haptic tick.
 *     Use from a ref: `ref={(el) => pressable(el)}`.
 *
 *   - `.motion-pressable` CSS class (see motion.css): pure-CSS press with a
 *     springy release curve. Zero JS — for cards/rows/chips en masse.
 *
 * Reduced motion: the spring jumps to the final value (no visible scaling),
 * and the CSS class is neutralised by the global guard.
 */

import { onCleanup } from "solid-js";

import { vibrate } from "@/lib/gestures/haptics";

import { createRetargetableSpring, SPRINGS, type SpringConfig } from "./spring";

interface PressableOptions {
  /** Scale while pressed. @default 0.96 */
  scale?: number;
  /** Haptic tick duration (ms) on press; 0 disables. @default 0 */
  hapticMs?: number;
  /** Spring for the press-in phase. @default SPRINGS.press */
  pressConfig?: SpringConfig;
  /** Spring for the release phase. @default SPRINGS.release */
  releaseConfig?: SpringConfig;
}

const PRESSED_SCALE = 0.96;

/**
 * Attach spring press feedback to an element. Solid-aware: cleans up with the
 * owning reactive scope (call it from a `ref` callback).
 */
export function pressable(el: HTMLElement, options: PressableOptions = {}): void {
  const pressedScale = options.scale ?? PRESSED_SCALE;
  const pressConfig = options.pressConfig ?? SPRINGS.press;
  const releaseConfig = options.releaseConfig ?? SPRINGS.release;

  const spring = createRetargetableSpring(1, (value) => {
    // Compositor-only write; `scale3d` keeps the layer on the GPU.
    if (value === 1) {
      // Settled: clear the inline transform + hint so we do not hold a
      // compositor layer (or fight other transform styling) at rest.
      el.style.transform = "";
      el.style.willChange = "";
    } else {
      el.style.transform = `scale3d(${value}, ${value}, 1)`;
    }
  });

  let pressed = false;

  const press = (event: PointerEvent) => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }
    pressed = true;
    el.style.willChange = "transform";
    if (options.hapticMs) {
      vibrate(options.hapticMs);
    }
    spring.setTarget(pressedScale, pressConfig);
  };

  const release = () => {
    if (!pressed) {
      return;
    }
    pressed = false;
    spring.setTarget(1, releaseConfig);
  };

  el.addEventListener("pointerdown", press, { passive: true });
  el.addEventListener("pointerup", release, { passive: true });
  el.addEventListener("pointercancel", release, { passive: true });
  el.addEventListener("pointerleave", release, { passive: true });

  onCleanup(() => {
    el.removeEventListener("pointerdown", press);
    el.removeEventListener("pointerup", release);
    el.removeEventListener("pointercancel", release);
    el.removeEventListener("pointerleave", release);
    spring.dispose();
  });
}
